/**
 * Webflow-authored Editorial content the Tool shows but never fetches
 * (Website ADR 0003): Election narratives and Candidate profiles, rendered
 * into hidden Collection Lists on the Elections page. Any number of lists may
 * carry them, since one list renders at most 100 items.
 */

export interface Profile {
  bio: DocumentFragment;
  photoSrc: string | null;
  photoAlt: string | null;
}

/** The element whose `attr` equals `slug` exactly — compared, never interpolated into a selector. */
function findKeyed(attr: string, slug: string, root: ParentNode): Element | null {
  for (const el of Array.from(root.querySelectorAll(`[${attr}]`))) {
    if (el.getAttribute(attr) === slug) return el;
  }
  return null;
}

/**
 * A copy of the element's children; the hidden list stays intact. The profile
 * photo is left out: a cloned `<img>` outside the hidden list would download
 * and render, and the Tool builds its own image.
 */
function cloneChildren(el: Element): DocumentFragment {
  const fragment = el.ownerDocument.createDocumentFragment();
  for (const child of Array.from(el.childNodes)) {
    const copy = child.cloneNode(true);
    if (isElement(copy)) {
      if (copy.matches(PHOTO_MARKER)) continue;
      for (const photo of Array.from(copy.querySelectorAll(PHOTO_MARKER))) photo.remove();
    }
    fragment.appendChild(copy);
  }
  return fragment;
}

function isElement(node: Node): node is Element {
  return node.nodeType === 1;
}

function attrOrNull(el: Element, name: string): string | null {
  const value = el.getAttribute(name)?.trim();
  return value ? value : null;
}

const PHOTO_MARKER = "[data-profile-photo]";

/**
 * The profile's photo, and the one place to add another source later (e.g.
 * Backbone's Person photo as a fallback). Attributes on the carrier win, so
 * pages built against the older contract keep working; otherwise the native
 * Image element marked `data-profile-photo`. Its `src` attribute is read, not
 * `currentSrc` — the hidden image never loads. Webflow still renders the
 * element for an empty Photo field, with a placeholder `src` and the class
 * `w-dyn-bind-empty`; that counts as no photo.
 */
function profilePhoto(el: Element): Pick<Profile, "photoSrc" | "photoAlt"> {
  const img = el.querySelector(`img${PHOTO_MARKER}`);
  const bound = img && !img.classList.contains("w-dyn-bind-empty") && attrOrNull(img, "src") ? img : null;
  const attrSrc = attrOrNull(el, "data-photo-src");
  // The image's alt describes the image's photo, not one named on the carrier.
  const fromImage = !attrSrc && bound;
  return {
    photoSrc: attrSrc ?? (fromImage ? attrOrNull(fromImage, "src") : null),
    photoAlt: attrOrNull(el, "data-photo-alt") ?? (fromImage ? attrOrNull(fromImage, "alt") : null),
  };
}

export function getNarrative(cycleSlug: string, root: ParentNode = document): DocumentFragment | null {
  const el = findKeyed("data-election-narrative", cycleSlug, root);
  return el ? cloneChildren(el) : null;
}

export function getProfile(personSlug: string, root: ParentNode = document): Profile | null {
  const el = findKeyed("data-person-profile", personSlug, root);
  if (!el) return null;
  return { bio: cloneChildren(el), ...profilePhoto(el) };
}

/**
 * A mistyped profile slug fails silently on the page, so say so in the
 * console for any profile that matches no public Person. The browser never
 * sees every public slug, so callers pass only what they know: with `only`,
 * judge just those profiles (e.g. the one a not-found Person view looked up).
 */
export function warnOrphans(knownSlugs: Set<string>, root: ParentNode = document, only?: Iterable<string>): void {
  const scope = only ? new Set(only) : null;
  for (const el of Array.from(root.querySelectorAll("[data-person-profile]"))) {
    const slug = el.getAttribute("data-person-profile") ?? "";
    if (scope && !scope.has(slug)) continue;
    if (!knownSlugs.has(slug)) {
      console.warn(`[elections] Candidate profile "${slug}" matches no public Person — check its Person slug in Webflow.`);
    }
  }
}
